import React, { useEffect, useMemo, useRef, useState } from 'react';
import FaceSmileIcon from './icons/FaceSmileIcon';

// Reaction set for posts and zips.
// (No heart here: the heart is the Like button.)
const REACTION_EMOJIS = ['😂', '😮', '😡', '🤟', '😕'];

interface ReactionPickerProps {
  reactions?: { [userId: string]: string };
  currentUserId?: string;
  onReact: (emoji: string) => void;
  onPromptLogin?: () => void;
  // 'row' matches PostCard's horizontal like/comment row. 'column' matches
  // ZipCard's vertical TikTok-style icon rail (icon-over-label, popover
  // anchored to the right so it doesn't run off the right edge of the screen).
  variant?: 'row' | 'column';
}

// Shared tap-to-open reaction button + popover for feed content (posts,
// zips) - unlike the long-press picker on chat bubbles, feed cards use a
// plain tap since there's no existing long-press gesture on them to collide
// with.
const ReactionPicker: React.FC<ReactionPickerProps> = ({ reactions, currentUserId, onReact, onPromptLogin, variant = 'row' }) => {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const myReaction = currentUserId ? reactions?.[currentUserId] : undefined;
  const reactionCounts = useMemo(() => {
    if (!reactions) return [] as { emoji: string; count: number }[];
    const counts: Record<string, number> = {};
    Object.values(reactions).forEach((emoji) => {
      counts[emoji] = (counts[emoji] || 0) + 1;
    });
    return Object.entries(counts).map(([emoji, count]) => ({ emoji, count }));
  }, [reactions]);
  const totalCount = reactionCounts.reduce((sum, r) => sum + r.count, 0);

  useEffect(() => {
    if (!showPicker) return;
    const handleOutside = (e: Event) => {
      const target = e.target as Node;
      if (
        pickerRef.current && !pickerRef.current.contains(target) &&
        containerRef.current && !containerRef.current.contains(target)
      ) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleOutside, true);
    document.addEventListener('touchstart', handleOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside, true);
      document.removeEventListener('touchstart', handleOutside, true);
    };
  }, [showPicker]);

  const handleToggle = () => {
    if (!currentUserId) {
      onPromptLogin?.();
      return;
    }
    setShowPicker((prev) => !prev);
  };

  const isColumn = variant === 'column';

  // The card pops open from the reaction button: mounted just long enough to play the open and close animations.
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  // After the pop-in finishes the per-emoji delays are dropped so hovering/tapping an emoji reacts instantly.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!shown) { setSettled(false); return; }
    const t = window.setTimeout(() => setSettled(true), 520);
    return () => window.clearTimeout(t);
  }, [shown]);
  useEffect(() => {
    if (showPicker) {
      setMounted(true);
      const frame = window.requestAnimationFrame(() => window.requestAnimationFrame(() => setShown(true)));
      return () => window.cancelAnimationFrame(frame);
    }
    setShown(false);
    const t = window.setTimeout(() => setMounted(false), 200);
    return () => window.clearTimeout(t);
  }, [showPicker]);

  return (
    <div ref={containerRef} className={isColumn ? 'relative' : ''}>
      <button
        type="button"
        onClick={handleToggle}
        className={
          isColumn
            ? 'group flex flex-col items-center gap-1 touch-manipulation'
            : `flex h-12 shrink-0 items-center gap-1.5 rounded-full px-2 max-[359px]:gap-1 max-[359px]:px-1 transition-colors hover:bg-white/10 ${myReaction ? '' : 'text-white'}`
        }
        aria-label="React"
        aria-expanded={showPicker}
      >
        {(() => {
          const face = myReaction ? (
            <span className={isColumn ? 'text-[38px] leading-none' : 'text-2xl leading-none'}>{myReaction}</span>
          ) : (
            <FaceSmileIcon className={isColumn ? 'h-10 w-10 text-white' : 'h-9 w-9'} strokeWidth={isColumn ? 2.75 : undefined} />
          );
          return isColumn ? <span className="railbtn">{face}</span> : face;
        })()}
        <span className={isColumn ? 'text-[11px] font-extrabold text-white' : 'text-base font-black'}>
          {totalCount}
        </span>
      </button>
      {mounted && (
        <div
          ref={pickerRef}
          role="menu"
          aria-label="Pick a reaction"
          // Posts: a rounded rectangle (3 x 2) above the button. Zips: a tall rounded rectangle just left of the button rail.
          className={`absolute z-50 w-max border-2 border-white bg-black ${isColumn ? 'right-full top-1/2 mr-4 rounded-[1.75rem] p-2' : 'bottom-full -left-3 mb-3 rounded-[1.75rem] p-2'}`}
          style={{
            transformOrigin: isColumn ? 'right center' : 'bottom left',
            opacity: shown ? 1 : 0,
            transform: `${isColumn ? 'translateY(-50%) ' : ''}${shown ? 'scale(1)' : 'scale(0.7)'}`,
            transition: 'opacity 160ms ease, transform 280ms cubic-bezier(0.22, 1.2, 0.36, 1)',
            pointerEvents: shown ? 'auto' : 'none',
          }}
        >
          <div className={isColumn ? 'flex flex-col gap-1' : 'flex flex-row gap-1'}>
            {REACTION_EMOJIS.map((emoji, i) => (
              <button
                key={emoji}
                type="button"
                onClick={() => { onReact(emoji); setShowPicker(false); }}
                className={`group/emoji flex h-[3.75rem] w-[3.75rem] items-center justify-center rounded-2xl text-[2rem] leading-none transition-colors duration-150 hover:bg-white/10 active:scale-95 ${myReaction === emoji ? 'bg-white/20 ring-1 ring-white' : ''}`}
                aria-label={`React ${emoji}`}
              >
                <span
                  className="block transition-transform duration-150 group-hover/emoji:scale-125"
                  style={{
                    opacity: shown ? 1 : 0,
                    transform: shown ? undefined : 'scale(0.4)',
                    transition: settled ? 'transform 150ms ease' : `opacity 200ms ease ${60 + i * 28}ms, transform 300ms cubic-bezier(0.22, 1.3, 0.36, 1) ${60 + i * 28}ms`,
                  }}
                >
                {emoji}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReactionPicker;
